/**
 * Neuroscience derivation functions for Cities & Communities.
 * Same CREB→BDNF→outcomes cascade, urban-framed descriptions.
 */

const ACTIVATING_WEIGHTS = {
  movement: 0.30,
  enrichment: 0.25,
  nature: 0.25,
  daylight: 0.20,
};

const INHIBITING_WEIGHTS = {
  air_quality: 0.35,
  noise: 0.35,
  visual: 0.30,
};

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

export function computeActivating(pathwayScores) {
  let total = 0;
  for (const [key, weight] of Object.entries(ACTIVATING_WEIGHTS)) {
    total += (pathwayScores[key] ?? 0) * weight;
  }
  return Math.max(0, total);
}

export function computeInhibiting(pathwayScores) {
  let total = 0;
  for (const [key, weight] of Object.entries(INHIBITING_WEIGHTS)) {
    total += (pathwayScores[key] ?? 0) * weight;
  }
  return Math.min(0, total);
}

export function computeSynergy(pathwayScores) {
  const mov = pathwayScores.movement ?? 0;
  const enr = pathwayScores.enrichment ?? 0;
  const nat = pathwayScores.nature ?? 0;

  let synergy = 0;
  if (nat > 0 && mov > 0) synergy += 0.15 * Math.min(nat, mov);
  if (enr > 0 && mov > 0) synergy += 0.10 * Math.min(enr, mov);
  return synergy;
}

export function computeBdnfBreakdown(pathwayScores) {
  const activating = computeActivating(pathwayScores);
  const inhibiting = computeInhibiting(pathwayScores);
  const synergy = computeSynergy(pathwayScores);
  const netCreb = activating + inhibiting + synergy;
  return { activating, inhibiting, synergy, netCreb };
}

export function computePathwayContributions(pathwayScores) {
  const allWeights = {
    movement: 0.20,
    nature: 0.20,
    enrichment: 0.18,
    daylight: 0.12,
    wayfinding: 0.08,
    air_quality: 0.10,
    noise: 0.07,
    visual: 0.05,
  };

  return [
    'movement', 'nature', 'enrichment', 'daylight', 'wayfinding',
    'air_quality', 'noise', 'visual',
  ].map(key => {
    const raw = pathwayScores[key] ?? 0;
    const contribution = raw * (allWeights[key] ?? 0.1);
    return { key, contribution };
  });
}

export function computeTemporalOutcomes(pathwayScores) {
  const mov = pathwayScores.movement ?? 0;
  const nat = pathwayScores.nature ?? 0;
  const enr = pathwayScores.enrichment ?? 0;
  const day = pathwayScores.daylight ?? 0;
  const air = pathwayScores.air_quality ?? 0;
  const noise = pathwayScores.noise ?? 0;

  const immediate = clamp01(0.35 * mov + 0.25 * nat + 0.20 * enr + 0.20 * (1 + noise));
  const shortTerm = clamp01(0.30 * nat + 0.25 * (1 + air) + 0.20 * (1 + noise) + 0.25 * enr);
  const longTerm = clamp01(0.30 * enr + 0.25 * mov + 0.25 * nat + 0.10 * day + 0.10 * (1 + air));

  return {
    immediate: Math.round(immediate * 100),
    shortTerm: Math.round(shortTerm * 100),
    longTerm: Math.round(longTerm * 100),
  };
}

export function classifyNetEffect(score) {
  if (score >= 0.60) return { label: 'Strongly Positive', color: '#10b981', arrow: 'up' };
  if (score >= 0.45) return { label: 'Positive', color: '#22c55e', arrow: 'up' };
  if (score >= 0.30) return { label: 'Marginal', color: '#f59e0b', arrow: 'flat' };
  return { label: 'Negative', color: '#f43f5e', arrow: 'down' };
}

export function netEffectDescription(classification) {
  switch (classification.label) {
    case 'Strongly Positive':
      return 'This urban environment strongly promotes BDNF expression and community brain health through walkability, green infrastructure, and enriched public spaces';
    case 'Positive':
      return 'This urban environment supports BDNF expression and brain health outcomes for residents and visitors';
    case 'Marginal':
      return 'This urban environment has limited positive impact on community brain health — interventions in green infrastructure and walkability recommended';
    default:
      return 'This urban environment may inhibit brain health outcomes — comprehensive design interventions needed';
  }
}
